import { LogLevel, type ILogger } from '@microsoft/signalr';

export type SignalrAccessTokenFactory = () => string | Promise<string | null>;

export interface SignalrHttpOptions {
  withCredentials?: boolean;
  accessTokenFactory: () => Promise<string>;
}

/**
 * HTTP options for a cookie (or bearer) hub connection.
 *
 * SignalR automatic reconnect negotiates with its own HTTP client, so the Angular interceptor
 * never sees it. `ensureFreshCredentials` must run on **every** negotiate — including reconnect
 * after the access-token cookie expired — or the hub 401s and looks like a dropped session.
 *
 * An empty factory return adds no `Authorization` header (SignalR treats it as falsy), so cookie
 * sessions can use this as a before-negotiate hook while still sending `withCredentials`.
 */
export function buildSignalrHttpOptions(
  ensureFresh?: (() => Promise<void>) | null,
  accessTokenFactory?: SignalrAccessTokenFactory | null
): SignalrHttpOptions {
  return {
    withCredentials: accessTokenFactory ? undefined : true,
    accessTokenFactory: async () => {
      await ensureFresh?.();
      if (!accessTokenFactory) {
        return '';
      }
      return (await accessTokenFactory()) ?? '';
    },
  };
}

/**
 * Same delays as SignalR's default automatic reconnect policy
 * (`DefaultReconnectPolicy`: 0, 2s, 10s, 30s, then stop).
 */
export const SIGNALR_RETRY_DELAYS_MS = [0, 2000, 10000, 30000] as const;

/**
 * Retry delay for SignalR automatic reconnect.
 *
 * Returns `null` when the hub is intentionally stopped (logout) so a dropped
 * WebSocket is not treated as a transient failure that should come back.
 */
export function nextSignalrRetryDelay(
  enabled: boolean,
  previousRetryCount: number
): number | null {
  if (!enabled) {
    return null;
  }
  if (previousRetryCount < 0 || previousRetryCount >= SIGNALR_RETRY_DELAYS_MS.length) {
    return null;
  }
  return SIGNALR_RETRY_DELAYS_MS[previousRetryCount];
}

/**
 * Let an in-flight `start()` finish before `stop()`. Calling `stop()` during negotiate
 * prints SignalR's "Failed to start the connection: The connection was stopped during
 * negotiation" — that is the login-page race when dashboard/sidebar connect and session
 * teardown (or a second connect) stops the same hub.
 */
export async function waitForInFlightHubStart(
  inFlight: Promise<unknown> | null | undefined
): Promise<void> {
  if (!inFlight) {
    return;
  }
  try {
    await inFlight;
  } catch {
    // start failed or was abandoned; caller still stops the socket
  }
}

/**
 * SignalR logger that goes quiet once the hub is no longer wanted (logout / session end),
 * so Utils.js 1006 and negotiate-401 lines do not print on the login page.
 */
export function createSignalrLogger(isEnabled: () => boolean): ILogger {
  return {
    log(logLevel: LogLevel, message: string): void {
      if (!isEnabled()) {
        return;
      }
      if (logLevel === LogLevel.Error) {
        console.error(message);
        return;
      }
      if (logLevel >= LogLevel.Warning) {
        console.warn(message);
      }
    },
  };
}
